import React, { useCallback, useState } from 'react';
import {
  AdornedInput,
  Adornment,
  Checkbox,
  DatePicker,
  DatePickerInput,
  DatePickerProps,
  DropdownSelect,
  Flex,
  FormField,
  FormHelp,
  Grid,
  IconCalendarOutline,
  Label,
  LabeledControl,
  Switch,
  TextButton,
} from '@zillow/constellation';
import { useField } from 'formik';
import {
  determineModeFromInitialValues,
  isDateSelectionMode,
  isRangeOfMultipleDates,
  TDateSelectionMode,
} from './util/utils';

const DATE_PLACEHOLDER = 'MM/DD/YYYY';

// this is a complex form field component.
// we have, in effect, four different components:
// 1. Date Range (from start to end)
// 2. Precise Date (from start to start + 1)
// 3. Before Date (less than end)
// 4. After Date (greater than start)
export const MultiDateConstellationField: React.FC<{ startName: string; endName: string; label?: React.ReactNode }> = ({
  startName,
  endName,
  label,
}) => {
  const [startField] = useField<string>({ name: startName });
  const [endField] = useField<string>({ name: endName });
  const [isRange, setIsRange] = useState<boolean>(() => {
    return isRangeOfMultipleDates(startField.value, endField.value);
  });
  if (isRange) {
    return (
      <RangePickerField
        isRange={isRange}
        setIsRange={setIsRange}
        startName={startName}
        endName={endName}
        label={label}
      />
    );
  }
  return (
    <DateAndModePicker
      isRange={isRange}
      setIsRange={setIsRange}
      startName={startName}
      endName={endName}
      label={label}
    />
  );
};

const RangePickerField: React.FC<{
  isRange: boolean;
  setIsRange: React.Dispatch<React.SetStateAction<boolean>>;
  startName: string;
  endName: string;
  label?: React.ReactNode;
}> = ({ isRange, setIsRange, startName, endName, label }) => {
  const [endField, endMeta, endHelpers] = useField<string>({ name: endName });
  const onChangeEnd = useCallback(
    (value: string) => {
      endHelpers.setValue(value);
    },
    [endHelpers]
  );

  const onClearEnd = useCallback(() => {
    endHelpers.setValue('');
  }, [endHelpers]);

  return (
    <Grid display={'grid'} gridTemplateColumns={'repeat(2, 1fr)'} gap={'xs'}>
      <ControlledDatePickerWithRangeSwitch
        isRange={isRange}
        setIsRange={setIsRange}
        name={startName}
        label={label}
        startName={startName}
        endName={endName}
      />
      <ControlledDatePicker
        label={<Label>&nbsp;</Label>}
        onChange={onChangeEnd}
        value={endField.value}
        error={endMeta.error}
        onClear={onClearEnd}
      />
    </Grid>
  );
};

interface DatePickerAndSwitchProps extends DatePickerProps {
  isRange: boolean;
  setIsRange: React.Dispatch<React.SetStateAction<boolean>>;
  startName: string;
  endName: string;
  onClearProp: () => void;
}

const DatePickerWithRangeSwitch: React.FC<DatePickerAndSwitchProps> = ({
  isRange,
  setIsRange,
  startName,
  endName,
  onClearProp,
  ...datePickerProps
}) => {
  const [, , startHelpers] = useField<string>({ name: startName });
  const [, , endHelpers] = useField<string>({ name: endName });
  const onChange = useCallback(() => {
    setIsRange((prev) => !prev);
    startHelpers.setValue('');
    endHelpers.setValue('');
  }, [setIsRange, startHelpers, endHelpers]);

  return (
    <DatePickerWithClearDateTextButton onClear={onClearProp} {...datePickerProps}>
      <LabeledControl
        label={<Label>Date Range</Label>}
        control={<Switch checkbox={<Checkbox checked={isRange} onChange={onChange} />} />}
        paddingX={'sm'}
      />
    </DatePickerWithClearDateTextButton>
  );
};

const modeOptions: TDateSelectionMode[] = ['Selected Date', 'Before Date', 'After Date'];
const DateAndModePicker: React.FC<{
  isRange: boolean;
  setIsRange: React.Dispatch<React.SetStateAction<boolean>>;
  startName: string;
  endName: string;
  label?: React.ReactNode;
}> = ({ startName, endName, setIsRange, label, isRange }) => {
  const [startField, , startHelpers] = useField<string>({ name: startName });
  const [endField, , endHelpers] = useField<string>({ name: endName });
  const [singleDateMode, setSingleDateMode] = useState<TDateSelectionMode>(() =>
    determineModeFromInitialValues(startField.value, endField.value)
  );
  const onChangeMode = useCallback(
    (nextValue: unknown) => {
      if (isDateSelectionMode(nextValue)) {
        setSingleDateMode(nextValue);
        startHelpers.setValue('');
        endHelpers.setValue('');
      }
    },
    [endHelpers, startHelpers, setSingleDateMode]
  );

  return (
    <Grid display={'grid'} gridTemplateColumns={'repeat(2, 1fr)'} gap={'xs'}>
      <DatePickerSelectedByMode
        singleDateMode={singleDateMode}
        isRange={isRange}
        setIsRange={setIsRange}
        startName={startName}
        endName={endName}
        label={label}
      />
      <FormField label={<Label>&nbsp;</Label>}>
        <DropdownSelect options={modeOptions} value={singleDateMode} onChange={onChangeMode} />
      </FormField>
    </Grid>
  );
};

const DatePickerSelectedByMode: React.FC<{
  singleDateMode: TDateSelectionMode;
  isRange: boolean;
  setIsRange: React.Dispatch<React.SetStateAction<boolean>>;
  startName: string;
  endName: string;
  label?: React.ReactNode;
}> = ({ singleDateMode, isRange, setIsRange, startName, endName, label }) => {
  if (singleDateMode === 'Selected Date') {
    return (
      <SelectedDatePicker
        isRange={isRange}
        setIsRange={setIsRange}
        startName={startName}
        endName={endName}
        label={label}
      />
    );
  }

  if (singleDateMode === 'Before Date') {
    return (
      <ControlledDatePickerWithRangeSwitch
        isRange={isRange}
        setIsRange={setIsRange}
        name={endName}
        label={label}
        startName={startName}
        endName={endName}
      />
    );
  }
  return (
    <ControlledDatePickerWithRangeSwitch
      isRange={isRange}
      setIsRange={setIsRange}
      name={startName}
      label={label}
      startName={startName}
      endName={endName}
    />
  );
};

const SelectedDatePicker: React.FC<{
  label?: React.ReactNode;
  isRange: boolean;
  setIsRange: React.Dispatch<React.SetStateAction<boolean>>;
  startName: string;
  endName: string;
}> = ({ label, isRange, setIsRange, startName, endName }) => {
  const [, , startHelpers] = useField<string>({ name: startName });
  const [, , endHelpers] = useField<string>({ name: endName });

  const onChange = useCallback(
    (value: string) => {
      startHelpers.setValue(value);
      endHelpers.setValue(value);
    },
    [startHelpers, endHelpers]
  );
  const onClearProp = useCallback(() => {
    startHelpers.setValue('');
    endHelpers.setValue('');
  }, [startHelpers, endHelpers]);

  return (
    <ControlledDatePickerWithRangeSwitch
      label={label}
      onChangeProp={onChange}
      onClearProp={onClearProp}
      name={startName}
      setIsRange={setIsRange}
      isRange={isRange}
      endName={endName}
      startName={startName}
    />
  );
};

const ControlledDatePicker: React.FC<{
  label?: React.ReactNode;
  onChange: (value: string) => void;
  value: string;
  error?: string;
  onClear: () => void;
}> = ({ label, value, onChange, error, onClear }) => {
  return (
    <FormField
      label={label}
      control={
        <AdornedInput
          input={
            <DatePickerInput
              placeholder={DATE_PLACEHOLDER}
              onChange={onChange}
              value={value}
              datepicker={<DatePickerWithClearDateTextButton onClear={onClear} />}
            />
          }
          rightAdornment={
            <Adornment>
              <IconCalendarOutline size="sm" />
            </Adornment>
          }
        />
      }
      error={!!error}
      description={<FormHelp>{error}</FormHelp>}
    />
  );
};

const ControlledDatePickerWithRangeSwitch: React.FC<{
  label?: React.ReactNode;
  isRange: boolean;
  setIsRange: React.Dispatch<React.SetStateAction<boolean>>;
  name: string;
  onChangeProp?: (value: string) => void;
  startName: string;
  endName: string;
  onClearProp?: () => void;
}> = ({ label, isRange, setIsRange, name, onChangeProp, startName, endName, onClearProp }) => {
  const [field, meta, helpers] = useField<string>({ name });
  const onChange = useCallback(
    (value: string) => {
      if (onChangeProp) {
        onChangeProp(value);
        return;
      }
      helpers.setValue(value);
    },
    [helpers, onChangeProp]
  );
  const onClear = useCallback(() => {
    if (onClearProp) {
      onClearProp();
      return;
    }
    helpers.setValue('');
  }, [onClearProp, helpers]);

  return (
    <FormField
      label={label}
      control={
        <AdornedInput
          input={
            <DatePickerInput
              onChange={onChange}
              value={field.value}
              placeholder={DATE_PLACEHOLDER}
              datepicker={
                <DatePickerWithRangeSwitch
                  isRange={isRange}
                  setIsRange={setIsRange}
                  startName={startName}
                  endName={endName}
                  onClearProp={onClear}
                />
              }
            />
          }
          rightAdornment={
            <Adornment>
              <IconCalendarOutline size="sm" />
            </Adornment>
          }
        />
      }
      error={!!meta.error}
      description={<FormHelp>{meta.error}</FormHelp>}
    />
  );
};

interface DatePickerWithClearDateTextButtonProps extends DatePickerProps {
  onClear: () => void;
  children?: React.ReactNode;
}
const DatePickerWithClearDateTextButton: React.FC<DatePickerWithClearDateTextButtonProps> = ({
  onClear,
  children,
  ...datePickerProps
}) => {
  return (
    <Flex display={'flex'} flexDirection={'column'} paddingBottom={'sm'}>
      <DatePicker {...datePickerProps} />
      <TextButton onClick={onClear}>Clear Date</TextButton>
      {children}
    </Flex>
  );
};
